import { APPROVAL_STATUS, EVENT_TYPES } from './api';
import { DEFAULT_TIME_ZONE, dayKeyInZone, formatHmInZone } from '../lib/workerTime';

// ── Textos de confirmación / error de las acciones del usuario ─────────────
// Catálogo ÚNICO de los mensajes que se ven en los toasts (components/Toast).
// Las páginas no montan frases a mano: llaman a estas funciones con los
// datos de lo que ha pasado DE VERDAD (p.ej. si lo creado queda pendiente de
// aprobación) y un detalle barato (fecha, trabajador, número de elementos).
//
// Las fechas llegan como clave de día 'YYYY-MM-DD' (o un instante ISO + la
// zona del trabajador) y se pintan cortas: "12 oct", "12–16 oct".

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

export const DEFAULT_ERROR_MESSAGE = 'No se pudo completar la operación. Inténtalo de nuevo.';

// ── Utilidades ──────────────────────────────────────────────────────────────

export const plural = (n, one, many) => `${n} ${Number(n) === 1 ? one : many}`;

// true si el evento/solicitud queda PENDIENTE de aprobación. Acepta el
// número de estado o cualquier objeto con `approvalStatus` (payload o
// respuesta del backend; el backend a veces lo devuelve como string "0").
export const isPendingApproval = (x) => {
  const v = x != null && typeof x === 'object' ? x.approvalStatus : x;
  if (v == null || v === '') return false;
  return Number(v) === APPROVAL_STATUS.PENDING;
};

const pendingSuffix = (pending) => (pending ? ' Pendiente de aprobación.' : '');

// Clave de día a partir de 'YYYY-MM-DD…' o de un instante (en la zona `tz`).
const toDayKey = (v, tz = DEFAULT_TIME_ZONE) => {
  if (!v) return null;
  if (typeof v === 'string' && DAY_KEY_RE.test(v) && v.length === 10) return v;
  return dayKeyInZone(v, tz) || null;
};

const splitKey = (key) => {
  const m = DAY_KEY_RE.exec(String(key || ''));
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
};

// "12 oct" (sin año). Si no se puede interpretar, devuelve el texto tal cual.
export const fmtDayShort = (v, tz = DEFAULT_TIME_ZONE) => {
  const p = splitKey(toDayKey(v, tz));
  return p ? `${p.d} ${MONTHS[p.m - 1]}` : String(v || '');
};

// "12–16 oct" · "30 sep – 2 oct" · "30 dic 2026 – 2 ene 2027" · "12 oct".
export const fmtDayRange = (a, b, tz = DEFAULT_TIME_ZONE) => {
  const ka = toDayKey(a, tz);
  const kb = toDayKey(b, tz) || ka;
  const pa = splitKey(ka);
  const pb = splitKey(kb);
  if (!pa) return String(a || '');
  if (!pb || ka === kb) return fmtDayShort(ka);
  if (pa.y !== pb.y) return `${pa.d} ${MONTHS[pa.m - 1]} ${pa.y} – ${pb.d} ${MONTHS[pb.m - 1]} ${pb.y}`;
  if (pa.m !== pb.m) return `${pa.d} ${MONTHS[pa.m - 1]} – ${pb.d} ${MONTHS[pb.m - 1]}`;
  return `${pa.d}–${pb.d} ${MONTHS[pa.m - 1]}`;
};

// 90 → "1h 30m" · 120 → "2h" · 45 → "45m".
export const fmtMinutes = (mins) => {
  const total = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, '0')}m`;
};

const hm = (iso, tz = DEFAULT_TIME_ZONE) => (iso ? formatHmInZone(iso, tz) : '');

// Texto del error para el usuario. ApiError (services/api) ya trae el
// mensaje traducido y sin códigos HTTP; si no hay mensaje, `fallback`.
export const errorMessage = (err, fallback = DEFAULT_ERROR_MESSAGE) => {
  if (typeof err === 'string') return err.trim() || fallback;
  const msg = err && typeof err.message === 'string' ? err.message.trim() : '';
  return msg || fallback;
};

// Errores que NO se notifican con toast: la sesión caducada ya la gestiona
// AuthContext (cierra la sesión y lo explica en el login).
export const isSilentError = (err) => !!err && typeof err === 'object' && err.reason === 'session-ended';

// Tipo de toast para un resumen de operación masiva.
export const outcomeType = (ok, failed) => {
  if (!failed) return 'success';
  return ok > 0 ? 'warning' : 'error';
};

const EVENT_NOUN = {
  [EVENT_TYPES.CLOCK_IN]: 'entrada',
  [EVENT_TYPES.CLOCK_OUT]: 'salida',
  [EVENT_TYPES.PAUSE_START]: 'inicio de pausa',
  [EVENT_TYPES.PAUSE_END]: 'fin de pausa',
  [EVENT_TYPES.VACATION_START]: 'inicio de vacaciones',
  [EVENT_TYPES.VACATION_END]: 'fin de vacaciones',
  [EVENT_TYPES.SICK_LEAVE_START]: 'inicio de día completo',
  [EVENT_TYPES.SICK_LEAVE_END]: 'fin de día completo',
  [EVENT_TYPES.COMPENSATION_START]: 'inicio de compensación',
  [EVENT_TYPES.COMPENSATION_END]: 'fin de compensación',
  [EVENT_TYPES.ABSENCE_START]: 'inicio de ausencia por horas',
  [EVENT_TYPES.ABSENCE_END]: 'fin de ausencia por horas',
};
export const eventNoun = (eventType) => EVENT_NOUN[Number(eventType)] || 'fichaje';

// Qué se aprueba/rechaza, en minúscula: "vacaciones", "fichaje manual de entrada"…
export const requestSubject = (eventType) => {
  switch (Number(eventType)) {
    case EVENT_TYPES.VACATION_START:
    case EVENT_TYPES.VACATION_END: return 'vacaciones';
    case EVENT_TYPES.SICK_LEAVE_START:
    case EVENT_TYPES.SICK_LEAVE_END: return 'día completo';
    case EVENT_TYPES.ABSENCE_START:
    case EVENT_TYPES.ABSENCE_END: return 'ausencia por horas';
    case EVENT_TYPES.COMPENSATION_START:
    case EVENT_TYPES.COMPENSATION_END: return 'compensación';
    default: return `fichaje manual de ${eventNoun(eventType)}`;
  }
};

const forWorker = (name) => (name ? ` para ${name}` : '');
const ofWorker = (name) => (name ? ` de ${name}` : '');
const quoted = (name) => (name ? ` «${name}»` : '');

// Tipos de solicitud de periodo (CalendarioPage / AssignEventModal).
const PERIOD_NOUN = {
  vacaciones: 'vacaciones',
  baja: 'día completo',
  ausencia: 'ausencia por horas',
  compensacion: 'día de compensación',
};

// ── Catálogo ────────────────────────────────────────────────────────────────

export const FEEDBACK = {
  // Fichaje en vivo desde el panel (Dashboard).
  clock: (eventType, iso, tz) => {
    const at = hm(iso, tz);
    const t = at ? ` a las ${at}` : '';
    switch (Number(eventType)) {
      case EVENT_TYPES.CLOCK_IN: return `Entrada registrada${t}.`;
      case EVENT_TYPES.CLOCK_OUT: return `Salida registrada${t}. Jornada cerrada.`;
      case EVENT_TYPES.PAUSE_START: return `Pausa iniciada${t}.`;
      case EVENT_TYPES.PAUSE_END: return `Pausa finalizada${t}. Sigues en jornada.`;
      default: return `Fichaje registrado${t}.`;
    }
  },

  // Fichaje manual del propio trabajador.
  manualEntry: ({ eventType, dayKey, time, pending }) =>
    `Fichaje manual de ${eventNoun(eventType)} registrado (${fmtDayShort(dayKey)}${time ? `, ${time}` : ''}).${pendingSuffix(pending)}`,

  // Anulación por el propio trabajador de un fichaje SUYO pendiente
  // (reglas en src/utils/ownEventCancel.js). Solo pendientes: los aprobados
  // se cambian por solicitud de corrección.
  ownEventCancelled: ({ eventType, dayKey, time }) =>
    `Solicitud de ${eventNoun(eventType)} anulada (${fmtDayShort(dayKey)}${time ? `, ${time}` : ''}).`,
  ownEventAlreadyGone: 'Ese fichaje ya no existe: se había eliminado antes. Hemos actualizado la lista.',

  // Solicitud de corrección (correcciones + fichajes que faltaban).
  correctionRequest: ({ dayKey, count }) =>
    `Solicitud de corrección enviada para el ${fmtDayShort(dayKey)} (${plural(count, 'cambio', 'cambios')}). Pendiente de aprobación.`,
  correctionPartialFailure: (sent, err) =>
    `Se enviaron ${sent} de los cambios antes del error: ${errorMessage(err)}`,

  // Solicitudes de periodo del trabajador (vacaciones, día completo, por horas).
  periodRequest: ({ kind, start, end, startTime, endTime, pending = true }) => {
    const when = kind === 'ausencia' && startTime && endTime
      ? `${fmtDayShort(start)}, ${startTime}–${endTime}`
      : fmtDayRange(start, end);
    const head = kind === 'baja'
      ? 'Ausencia de día completo comunicada'
      : `Solicitud de ${PERIOD_NOUN[kind] || 'ausencia'} enviada`;
    return `${head} (${when}).${pendingSuffix(pending)}`;
  },
  attachmentUploaded: (fileName) => `Justificante${quoted(fileName)} subido.`,
  attachmentFailed: (reason) =>
    `La solicitud se ha registrado, pero el justificante no se subió: ${errorMessage(reason, 'error desconocido')}`,
  attachmentNoCompany:
    'La solicitud se ha registrado, pero el justificante no se subió: tu cuenta no está vinculada a una empresa.',

  // Edición / borrado de vacaciones (propias o como admin).
  vacationEdited: ({ start, end, pending, workerName }) => (pending
    ? `Cambio de vacaciones enviado (${fmtDayRange(start, end)}). Pendiente de aprobación.`
    : `Vacaciones${ofWorker(workerName)} actualizadas (${fmtDayRange(start, end)}).`),
  vacationDeleteRequested: ({ start, end }) =>
    `Solicitud de eliminación de vacaciones enviada (${fmtDayRange(start, end)}). Pendiente de aprobación.`,
  vacationDeleted: ({ start, end, workerName }) =>
    `Vacaciones${ofWorker(workerName)} eliminadas (${fmtDayRange(start, end)}).`,
  workerNotNotified: 'El cambio está guardado, pero no se pudo avisar al trabajador por mensaje.',

  // Asignaciones del admin.
  periodAssigned: ({ kind, start, end, workerName }) => {
    const who = workerName ? ` a ${workerName}` : '';
    const when = fmtDayRange(start, end);
    if (kind === 'vacaciones') return `Vacaciones asignadas${who} (${when}).`;
    if (kind === 'compensacion') return `Día de compensación asignado${who} (${when}).`;
    return `Día completo asignado${who} (${when}).`;
  },
  // Compensación POR TRAMOS (no día libre entero): las horas quedan
  // justificadas y se descuentan de la bolsa.
  compensationHoursAssigned: ({ dayKey, start, end, workerName }) =>
    `Horas compensadas registradas${forWorker(workerName)} (${fmtDayShort(dayKey)}, ${start}–${end}). `
    + 'Se descuentan de su bolsa de horas.',
  // Ausencia POR HORAS aprobada por el admin (par 10/11). Se RECUPERA: no
  // reduce la jornada esperada ni toca la bolsa, el trabajador las debe.
  hourlyAbsenceAssigned: ({ dayKey, start, end, workerName }) =>
    `Ausencia por horas registrada${forWorker(workerName)} (${fmtDayShort(dayKey)}, ${start}–${end}). `
    + 'Se recuperan: la jornada de ese día no se reduce.',

  manualEventAssigned: ({ eventType, dayKey, time, workerName }) =>
    `Fichaje de ${eventNoun(eventType)} registrado${forWorker(workerName)} (${fmtDayShort(dayKey)}${time ? `, ${time}` : ''}).`,

  // Edición de un fichaje (admin).
  eventEdited: ({ eventType, dayKey, time, approvalStatus }) => {
    let tail = '';
    if (Number(approvalStatus) === APPROVAL_STATUS.PENDING) tail = ' Queda pendiente de aprobación.';
    else if (Number(approvalStatus) === APPROVAL_STATUS.REJECTED) tail = ' Queda marcado como rechazado.';
    return `Fichaje actualizado: ${eventNoun(eventType)} · ${fmtDayShort(dayKey)}${time ? `, ${time}` : ''}.${tail}`;
  },

  // Borrado masivo de fichajes → { type, message }.
  eventsDeleted: (ok, failed) => {
    const type = outcomeType(ok, failed);
    if (!failed) return { type, message: ok === 1 ? 'Fichaje eliminado.' : `${ok} fichajes eliminados.` };
    if (ok === 0) return { type, message: `No se pudo eliminar ${failed === 1 ? 'el fichaje' : `ninguno de los ${failed} fichajes`}.` };
    return { type, message: `${plural(ok, 'fichaje eliminado', 'fichajes eliminados')}; ${failed} no se ${failed === 1 ? 'pudo' : 'pudieron'} eliminar.` };
  },

  // Bolsa de horas.
  hourBalanceAdjusted: ({ minutes, credit, dayKey, workerName }) =>
    `Ajuste de bolsa guardado: ${credit ? '+' : '−'}${fmtMinutes(minutes)}${forWorker(workerName)} (${fmtDayShort(dayKey)}).`,
  hourBalanceDeleted: 'Ajuste de bolsa eliminado.',

  // Fecha de inicio de cómputo (alta del trabajador a efectos de bolsa).
  // Se guarda como fila marcadora en /api/hourBalance (offset 0), ver
  // COMPUTATION_START_REASON en src/lib/hourBalance.js.
  computationStartSaved: ({ dayKey, workerName }) =>
    `Inicio de cómputo guardado: ${fmtDayShort(dayKey)}${forWorker(workerName)}. Los días anteriores dejan de contar en la bolsa.`,
  computationStartCleared: ({ workerName } = {}) =>
    `Inicio de cómputo eliminado${forWorker(workerName)}. La bolsa vuelve a contar desde el primer fichaje.`,
  // Los marcadores viejos no se pudieron borrar: la fecha nueva YA manda
  // (gana la más reciente), así que no es un error bloqueante.
  computationStartStaleKept: (n) =>
    `Se guardó la fecha nueva, pero ${n === 1 ? 'quedó 1 marcador antiguo' : `quedaron ${n} marcadores antiguos`} sin borrar. Vuelve a intentarlo desde la ficha.`,
  // Alta de trabajador: el trabajador SÍ se creó, solo falló el marcador.
  computationStartOnCreateFailed: (err) =>
    `El trabajador se creó, pero no se pudo guardar la fecha de inicio de cómputo${err?.message ? `: ${err.message}` : ''}. Configúrala desde su ficha.`,

  // Aprobaciones.
  requestReviewed: ({ approved, subject, workerName, when }) =>
    `Solicitud ${approved ? 'aprobada' : 'rechazada'}: ${subject}${ofWorker(workerName)}${when ? ` (${when})` : ''}.`,
  // Solo día completo recuperable (las ausencias por horas no crean offset).
  recoverableDebited: (minutes) => ` Se han descontado ${fmtMinutes(minutes)} de la bolsa (recuperable).`,
  hourlyAbsenceRecovered: ' Las horas se recuperan: cuentan como deuda en la bolsa de ese día.',
  // `subject` (requestSubject) para las eliminaciones de otros periodos (día
  // completo, por horas, compensación); por defecto, vacaciones.
  vacationDeleteReviewed: ({ approved, workerName, when, subject = 'vacaciones' }) => {
    const whenTxt = when ? ` (${when})` : '';
    if (subject !== 'vacaciones') {
      return approved
        ? `Eliminación aprobada: se ha borrado el periodo de ${subject}${ofWorker(workerName)}${whenTxt}.`
        : `Eliminación rechazada: el periodo de ${subject}${ofWorker(workerName)}${whenTxt} se mantiene.`;
    }
    return approved
      ? `Eliminación aprobada: vacaciones${ofWorker(workerName)}${whenTxt} borradas.`
      : `Eliminación rechazada: las vacaciones${ofWorker(workerName)}${whenTxt} se mantienen.`;
  },
  // Eliminación de un FICHAJE suelto (entrada/salida) que el trabajador pide
  // desde su Informe horario. No es un periodo: se resuelve un único evento y,
  // mientras no se resuelva, el fichaje sigue contando en su jornada.
  clockDeleteReviewed: ({ approved, workerName, when, noun = 'fichaje' }) => {
    const whenTxt = when ? ` (${when})` : '';
    return approved
      ? `Eliminación aprobada: se ha borrado la ${noun}${ofWorker(workerName)}${whenTxt}.`
      : `Eliminación rechazada: la ${noun}${ofWorker(workerName)}${whenTxt} se mantiene y sigue contando.`;
  },
  correctionOriginalDeleted: 'Fichaje original eliminado y corrección cerrada.',
  correctionNotClosed: 'El fichaje se eliminó, pero la corrección no se pudo marcar como cerrada. Revísala en la lista.',

  // Trabajadores / empresas / sedes.
  workerSaved: ({ name, created }) => `Trabajador${quoted(name)} ${created ? 'creado' : 'actualizado'}.`,
  workerScheduleFailed: (err) => `Trabajador guardado, pero la jornada no se pudo guardar: ${errorMessage(err)}`,
  workerScheduleVersionFailed: (err) =>
    `Trabajador guardado, pero no se pudo crear la nueva versión de horario: ${errorMessage(err)}`,
  companySaved: ({ name, created }) => `Empresa${quoted(name)} ${created ? 'creada' : 'actualizada'}.`,
  companyDeleted: (name) => `Empresa${quoted(name)} eliminada.`,
  workplaceSaved: ({ name, created }) => `Sede${quoted(name)} ${created ? 'creada' : 'actualizada'}.`,
  workplaceDeleted: (name) => `Sede${quoted(name)} eliminada.`,

  // Convenios.
  agreementSaved: ({ name, created }) => `Convenio${quoted(name)} ${created ? 'creado' : 'actualizado'}.`,
  agreementDeleted: (name) => `Convenio${quoted(name)} eliminado.`,
  agreementAssigned: ({ agreementName, workerName, validFrom, validTo }) =>
    `Convenio${quoted(agreementName)} asignado${workerName ? ` a ${workerName}` : ''} ${validTo ? `(${fmtDayRange(validFrom, validTo)})` : `desde el ${fmtDayShort(validFrom)}`}.`,
  agreementAssignmentDeleted: (agreementName) => `Asignación del convenio${quoted(agreementName)} eliminada.`,
  agreementBulkAssigned: ({ ok, failed, agreementName }) => {
    const type = outcomeType(ok, failed);
    const head = `Convenio${quoted(agreementName)}`;
    if (!failed) return { type, message: `${head} asignado a ${plural(ok, 'trabajador', 'trabajadores')}.` };
    if (ok === 0) return { type, message: `No se pudo asignar el convenio a ninguno de los ${failed} trabajadores. Revisa el detalle.` };
    return { type, message: `${head} asignado a ${plural(ok, 'trabajador', 'trabajadores')}; ${failed} con error. Revisa el detalle.` };
  },

  // Horarios (versiones).
  scheduleVersionCreated: ({ validFrom, validTo }) =>
    `Horario guardado ${validTo ? `(${fmtDayRange(validFrom, validTo)})` : `desde el ${fmtDayShort(validFrom)}`}.`,
  scheduleVersionDeleted: 'Horario eliminado.',

  // Festivos.
  holidayCreated: (dayKey) => `Festivo local creado para el ${fmtDayShort(dayKey)}.`,
  holidayDeleted: ({ description, dayKey }) =>
    `Festivo${quoted(description)} ${dayKey ? `del ${fmtDayShort(dayKey)} ` : ''}eliminado.`,
  companyHolidaysImported: ({ created, deleted, years }) => {
    const ys = Array.isArray(years) && years.length ? ` para ${years.join(', ')}` : '';
    const replaced = deleted ? ` (sustituyen a ${deleted} anteriores)` : '';
    return `Festivos de empresa importados: ${created}${ys}${replaced}.`;
  },
  companyHolidayDeleted: ({ description, dayKey }) =>
    `Festivo de empresa${quoted(description)} del ${fmtDayShort(dayKey)} eliminado.`,

  // Mensajería.
  messageSent: (receiverName) => `Mensaje enviado${receiverName ? ` a ${receiverName}` : ''}.`,
  messageSentCompany: (companyName) => `Mensaje enviado a toda la empresa${quoted(companyName)}.`,
  messageSentWorkplace: (workplaceName) => `Mensaje enviado a toda la sede${quoted(workplaceName)}.`,
  messageSentAll: ({ ok, failed }) => {
    const type = outcomeType(ok, failed);
    if (!failed) return { type, message: `Mensaje enviado a ${plural(ok, 'empresa', 'empresas')}.` };
    return { type, message: `Mensaje enviado a ${plural(ok, 'empresa', 'empresas')}; falló en ${failed}.` };
  },
  anonymousSent: 'Denuncia anónima enviada.',

  // Contraseña.
  passwordReset: 'Contraseña actualizada. Ya puedes iniciar sesión con la nueva.',

  // Importaciones masivas → { type, message }.
  eventsImported: ({ created, skipped, failed, filesFailed = 0 }) => {
    const type = outcomeType(created, failed + filesFailed);
    const parts = [`${plural(created, 'fichaje importado', 'fichajes importados')}`];
    if (skipped) parts.push(`${skipped} omitido${skipped === 1 ? '' : 's'}`);
    if (failed) parts.push(`${failed} con error`);
    if (filesFailed) parts.push(`${plural(filesFailed, 'archivo sin importar', 'archivos sin importar')}`);
    return { type, message: `${parts.join(' · ')}.` };
  },
  workersImported: ({ created, skipped, failed, incidents = 0 }) => {
    const type = incidents && !failed ? 'warning' : outcomeType(created + skipped, failed);
    const parts = [`${created} creado${created === 1 ? '' : 's'}`];
    if (skipped) parts.push(`${skipped} omitido${skipped === 1 ? '' : 's'}`);
    if (failed) parts.push(`${failed} con error`);
    if (incidents) parts.push(plural(incidents, 'incidencia', 'incidencias'));
    return { type, message: `Importación finalizada: ${parts.join(' · ')}. Revisa el informe.` };
  },

  downloadFailed: 'No se pudo descargar el archivo.',
};

export default FEEDBACK;
